import { useI18n } from '@ggs/i18n'
import { isBoolean, isFunction, uniqueId } from 'lodash'
import dayjs from 'dayjs'
import * as Yup from 'yup'
import { useMemo } from 'react'
import zipcodes from 'zipcodes-regex'
import { PhoneNumberUtil } from 'google-libphonenumber'

/**
 * Transform boolean input to 'yes' / 'no' string.
 * @param {boolean} value Boolean flag
 * @return {string} Transformed value
 */
export const yesNoFromBoolean = (value) => {
  // eslint-disable-next-line no-nested-ternary
  return isBoolean(value) ? (value ? 'yes' : 'no') : ''
}

/**
 * @param {Object=} country
 * @return {*}
 */
export default function useValidators({ country = {} } = {}) {
  const { t } = useI18n()
  const today = useMemo(() => dayjs(), [])
  const phoneUtil = PhoneNumberUtil.getInstance()

  const yupString = Yup.string(t('global:form.error.validValue'))
  const yupNumber = Yup.number(t('global:form.error.validValue'))
  const yupBoolean = Yup.boolean(t('global:form.error.validValue'))
  const yupArray = Yup.array(t('global:form.error.validValue'))

  return {
    yupString,
    yupNumber,
    yupBoolean,
    yupArray,
    nonBlankArray: Yup.array()
      .min(1, t('global:form.error.required'))
      .required(t('global:form.error.required')),
    date: yupString.test(
      uniqueId('validDate_'),
      t('global:form.error.invalidDate'),
      (newValue, { path: name }) => {
        let result = true
        const input = document.querySelector(`input[name='${name}']`)
        // eslint-disable-next-line no-param-reassign
        if (!input.disabled) {
          const { value } = input
          const selectedDate = dayjs(value, 'YYYY-MM-DD')

          if (
            !value ||
            !selectedDate ||
            !selectedDate?.isValid() ||
            selectedDate?.isAfter(today) ||
            selectedDate?.format('YYYY-MM-DD') !== value
          ) {
            result = false
          }
        }

        // console.log('selectedDate', {
        //   result,
        //   fieldName,
        //   value,
        //   selectedDate: selectedDate.format('YYYY-MM-DD'),
        //   context,
        // })

        return result
      }
    ),
    username: yupString
      .min(4, t('global:form.error.minXCharactersLong', { min: 4 }))
      .max(32, t('global:form.error.maxXCharactersLong', { max: 32 }))
      .matches(/^[_.@A-Za-z0-9]*$/g, t('global:form.error.username'))
      .required(t('global:form.error.required')),
    password: yupString
      .min(8, t('global:form.error.minXCharactersLong', { min: 8 }))
      .max(32, t('global:form.error.maxXCharactersLong', { max: 32 }))
      .required(t('global:form.error.required')),
    firstName: yupString
      .min(2, t('global:form.error.minXCharactersLong', { min: 2 }))
      .max(20, t('global:form.error.maxXCharactersLong', { max: 20 }))
      .required(t('global:form.error.required')),
    lastName: yupString
      .min(2, t('global:form.error.minXCharactersLong', { min: 2 }))
      .max(20, t('global:form.error.maxXCharactersLong', { max: 20 }))
      .required(t('global:form.error.required')),
    email: yupString.email(t('global:form.error.email')).required(t('global:form.error.required')),
    // Check for all valid phone number types
    phoneNumber: Yup.string().test(
      uniqueId('validPhone_'),
      t('global:form.error.phoneNumber'),
      (newValue) => {
        const countryCode = country?.current || ''
        if (countryCode && newValue) {
          try {
            const number = phoneUtil.parseAndKeepRawInput(newValue, countryCode)
            return phoneUtil.isValidNumberForRegion(number, countryCode)
          } catch (e) {
            console.log('Phone util error:', e)
          }
        }

        return false
      }
    ),
    verificationCode: yupString.required(t('global:form.error.required')),
    postalCode: Yup.string(t('global:form.error.validValue'))
      .test(
        uniqueId('validPostal_'),
        t('global:form.error.postalCode'),
        (newValue, { path: name }) => {
          let result = true
          const input = document.querySelector(`input[name='${name}']`)
          // eslint-disable-next-line no-param-reassign
          if (!input.disabled) {
            const { value } = input
            const zipcode = String(country?.current ? zipcodes[country?.current] : '').replace(
              ' +?',
              ' *'
            )
            if (zipcode) {
              const regex = new RegExp(zipcode, 'gi')
              result = regex.test(value)

              console.log('zipcode check', {
                zipcode,
                result,
                regex,
                value,
                country,
              })
            }
          }

          return result
        }
      )
      .required(t('global:form.error.required')),
    amount: (min) =>
      yupNumber
        .min(min, t('global:form.error.minMaxUnlimited', { min }))
        .required(t('global:form.error.required')),
    yesNoOptions: yupString
      .oneOf(['yes', 'no'], t('global:form.error.required'))
      .required(t('global:form.error.required')),
    acceptTerms: yupBoolean
      .oneOf([true], t('global:form.error.acceptTerms'))
      .required(t('global:form.error.acceptTerms')),
    /**
     * Validate amount within mix/max range.
     * @param {Number} min
     * @param {Number} max
     * @param {function} formatter
     * @return {Yup.number}
     */
    minMaxAmount: (min, max, formatter = null) =>
      yupNumber
        .min(
          min,
          t('global:form.error.minXAmount').replace(
            '{amount}',
            isFunction(formatter) ? formatter(min) : min.toString()
          )
        )
        .max(
          max,
          t('global:form.error.maxXAmount').replace(
            '{amount}',
            isFunction(formatter) ? formatter(max) : max.toString()
          )
        ),

    nonBlank: yupString
      .min(1, t('global:form.error.minXCharactersLong', { min: 1 }))
      .required(t('global:form.error.required')),
    roleSelection: yupString
      .matches(/(participant|secondary_lead)/, {
        message: t('global:form.error.notValidRole'),
        excludeEmptyString: false,
      })
      .required(t('global:form.error.required')),
    basicPhone: yupString
      .min(7, t('global:form.error.minXCharactersLong', { min: 7 }))
      .max(20, t('global:form.error.maxXCharactersLong', { max: 20 }))
      .matches(/^[+]?[-\d]*$/g, t('global:form.error.phoneNumber'))
      .required(t('global:form.error.required')),
  }
}
